import { useState } from 'react';
import { IChecklistSection, IChecklistResponse } from '@/types/checklist.types';
import { ICompanyData } from '@/types/company-data.types';
import { useGetMediaByResponse, useUploadMedia, useDeleteMedia } from './useChecklistMedia';
import { ChecklistSection } from './components/ChecklistSection';
import { MediaAttachmentModal } from './components/MediaAttachmentModal';
import { env } from '@/utils/constants/env';

interface ChecklistViewProps {
  sections: IChecklistSection[];
  responses: IChecklistResponse[];
  companyData?: ICompanyData;
  tenantId: string | number;
  isSuperAdmin?: boolean;
  isReadOnlyDueToSubscription?: boolean;
  implementationStartedAt?: string | null;
  onUpdateResponse: (responseId: number, field: string, value: string) => void;
}

const MAX_ITEMS_PER_CATEGORY = 50;
const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_DOCUMENT_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'];
const ALLOWED_IMAGE_VIDEO_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.mp4', '.mov'];

export const ChecklistView = ({
  sections,
  responses,
  companyData,
  tenantId,
  isSuperAdmin = false,
  isReadOnlyDueToSubscription = false,
  implementationStartedAt = null,
  onUpdateResponse,
}: ChecklistViewProps) => {
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [mediaModalOpen, setMediaModalOpen] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'documents' | 'images' | 'links'>('documents');
  const [linkInput, setLinkInput] = useState('');
  const [linkError, setLinkError] = useState('');

  const isReadOnly = isSuperAdmin || isReadOnlyDueToSubscription;

  // Media hooks
  const { data: documentsData = [] } = useGetMediaByResponse(tenantId, mediaModalOpen || undefined, 'documents');
  const { data: imagesData = [] } = useGetMediaByResponse(tenantId, mediaModalOpen || undefined, 'images');
  const uploadMediaMutation = useUploadMedia();
  const deleteMediaMutation = useDeleteMedia();

  const handleDownload = async (mediaId: number, fileName: string) => {
    try {
      const downloadUrl = `${env.apiTasklistBaseUrl}/api/v1/compro/media/${mediaId}`;

      const { axiosInstance } = await import('@/utils/lib/axios');
      const response = await axiosInstance.get(downloadUrl, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file');
    }
  };

  const toggleSection = (sectionId: number) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Helper to find response for an item
  const getResponseForItem = (itemId: number): IChecklistResponse | undefined => {
    return responses.find(r => r.item_id === itemId);
  };

  // Helper to get total media count for a response
  const getMediaCount = (responseId: number): number => {
    const response = responses.find(r => r.id === responseId);
    const linksCount = (response?.document_urls || []).filter(url => url && url.trim() !== '').length;
    const mediaCount = response?.media?.length || 0;
    return mediaCount + linksCount;
  };

  // Helper to close modal
  const closeModal = () => {
    setMediaModalOpen(null);
    setActiveTab('documents');
    setLinkInput('');
    setLinkError('');
  };

  // Validate URL format
  const isValidUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !mediaModalOpen) return;
    if (isReadOnly) {
      e.target.value = '';
      return;
    }

    const currentCount = documentsData.length;
    if (currentCount >= MAX_ITEMS_PER_CATEGORY) {
      alert(`Maximum ${MAX_ITEMS_PER_CATEGORY} documents allowed`);
      e.target.value = '';
      return;
    }

    const filesToUpload = Array.from(files).slice(0, MAX_ITEMS_PER_CATEGORY - currentCount);

    for (const file of filesToUpload) {
      // Validate file type
      const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_DOCUMENT_EXTENSIONS.includes(fileExt)) {
        alert(`Invalid file type for ${file.name}. Allowed: ${ALLOWED_DOCUMENT_EXTENSIONS.join(', ')}`);
        continue;
      }

      // Validate file size
      if (file.size > MAX_DOCUMENT_SIZE) {
        alert(`File ${file.name} is too large. Maximum size: 10 MB`);
        continue;
      }

      // Upload file
      await uploadMediaMutation.mutateAsync({
        responseId: mediaModalOpen,
        collectionName: 'documents',
        file,
      });
    }

    e.target.value = ''; // Reset input
  };

  // Handle image/video upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !mediaModalOpen) return;
    if (isReadOnly) {
      e.target.value = '';
      return;
    }

    const currentCount = imagesData.length;
    if (currentCount >= MAX_ITEMS_PER_CATEGORY) {
      alert(`Maximum ${MAX_ITEMS_PER_CATEGORY} images/videos allowed`);
      e.target.value = '';
      return;
    }

    const filesToUpload = Array.from(files).slice(0, MAX_ITEMS_PER_CATEGORY - currentCount);

    for (const file of filesToUpload) {
      // Validate file type
      const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_IMAGE_VIDEO_EXTENSIONS.includes(fileExt)) {
        alert(`Invalid file type for ${file.name}. Allowed: ${ALLOWED_IMAGE_VIDEO_EXTENSIONS.join(', ')}`);
        continue;
      }

      // Upload file
      await uploadMediaMutation.mutateAsync({
        responseId: mediaModalOpen,
        collectionName: 'images',
        file,
      });
    }

    e.target.value = ''; // Reset input
  };

  // Handle link submission
  const handleLinkSubmit = () => {
    if (!mediaModalOpen) return;
    if (isReadOnly) return;

    const response = responses.find(r => r.id === mediaModalOpen);
    if (!response) return;

    const currentUrls = response.document_urls || [];
    if (currentUrls.length >= MAX_ITEMS_PER_CATEGORY) {
      alert(`Maximum ${MAX_ITEMS_PER_CATEGORY} links allowed`);
      return;
    }

    if (!linkInput.trim()) {
      setLinkError('Please enter a URL');
      return;
    }

    if (!isValidUrl(linkInput)) {
      setLinkError('Please enter a valid URL (http:// or https://)');
      return;
    }

    // Add new URL to existing array
    const updatedUrls = [...currentUrls, linkInput.trim()];
    onUpdateResponse(response.id, 'document_urls', JSON.stringify(updatedUrls));

    setLinkInput('');
    setLinkError('');
  };

  // Handle link deletion
  const handleDeleteLink = (responseId: number, urlToDelete: string) => {
    if (isReadOnly) return;
    const response = responses.find(r => r.id === responseId);
    if (!response) return;

    const currentUrls = response.document_urls || [];
    const updatedUrls = currentUrls.filter(url => url !== urlToDelete);
    onUpdateResponse(response.id, 'document_urls', JSON.stringify(updatedUrls));
  };

  return (
    <>
      <div className="rounded-xl overflow-x-auto border border-gray-200 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-900">
        <div className="min-w-full md:min-w-[740px]">
          {/* Desktop Header */}
          <div
            className="hidden md:grid md:grid-cols-[60px_minmax(200px,2fr)_140px_minmax(180px,1.2fr)_160px] text-xs font-semibold text-white uppercase tracking-wider border-b border-[#071753]"
            style={{ backgroundColor: '#071753' }}
          >
            <div className="p-3 pl-6 border-r border-white/10">No</div>
            <div className="p-3 border-r border-white/10">Implementasi AOS</div>
            <div className="p-3 border-r border-white/10">Batas Waktu</div>
            <div className="p-3 border-r border-white/10">Referensi</div>
            <div className="p-3 pr-6">Status</div>
          </div>

          <div className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700">
            {sections.map(section => {
              const isExpanded = expandedSections.has(section.id);
              const sectionItems = (section.sub_sections || []).flatMap(subSection => subSection.items || []);
              const completedCount = sectionItems.filter(item => {
                const response = getResponseForItem(item.id);
                return response?.status === 'done' || response?.is_completed;
              }).length;
              const totalItems = sectionItems.length;

              return (
                <ChecklistSection
                  key={section.id}
                  section={section}
                  isExpanded={isExpanded}
                  completedCount={completedCount}
                  totalItems={totalItems}
                  responses={responses}
                  companyData={companyData}
                  readOnlyExceptStatus={isSuperAdmin}
                  isFullReadOnly={isReadOnlyDueToSubscription}
                  implementationStartedAt={implementationStartedAt}
                  onToggle={() => toggleSection(section.id)}
                  onUpdateResponse={onUpdateResponse}
                  onOpenMedia={setMediaModalOpen}
                  getMediaCount={getMediaCount}
                />
              );
            })}
          </div>
        </div>
      </div>

      <MediaAttachmentModal
        isOpen={mediaModalOpen !== null}
        activeTab={activeTab}
        documentsData={documentsData}
        imagesData={imagesData}
        currentUrls={responses.find(r => r.id === mediaModalOpen)?.document_urls || []}
        linkInput={linkInput}
        linkError={linkError}
        isUploading={uploadMediaMutation.isLoading}
        isDeleting={deleteMediaMutation.isLoading}
        maxItemsPerCategory={MAX_ITEMS_PER_CATEGORY}
        allowedDocumentExtensions={ALLOWED_DOCUMENT_EXTENSIONS}
        allowedImageExtensions={ALLOWED_IMAGE_VIDEO_EXTENSIONS}
        readOnly={isReadOnly}
        onClose={closeModal}
        onTabChange={setActiveTab}
        onDocumentUpload={handleDocumentUpload}
        onImageUpload={handleImageUpload}
        onDownload={handleDownload}
        onDelete={mediaId => !isReadOnly && deleteMediaMutation.mutate({ mediaId })}
        onLinkInputChange={value => {
          setLinkInput(value);
          setLinkError('');
        }}
        onAddLink={handleLinkSubmit}
        onDeleteLink={url => mediaModalOpen && handleDeleteLink(mediaModalOpen, url)}
      />
    </>
  );
};

// Keep this at the end for backward compatibility
export default ChecklistView;
